import { makeCRMURL } from '@/lib/utils';
import type { CustomerLead } from '@/models/registry';
import { Button } from '../button';
import { LeadPriorityScoreBadge } from './lead-line';

export const LeadCustomerDetails = ({
  selectedCustomer,
}: {
  selectedCustomer: CustomerLead;
}) => {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="mb-4">Customer Details</h2>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="text-sm text-gray-500">Name</label>
            <p className="mt-1">
              {selectedCustomer.personal_details?.first_name}{' '}
              {selectedCustomer.personal_details?.last_name}
            </p>
          </div>
          <div className="flex flex-col gap-2 w-fit">
            {/* <label className="text-sm text-gray-500">Priority</label> */}
            {/* <p className="text-blue-600 mt-1">{selectedCustomer.priority}</p> */}
            <LeadPriorityScoreBadge
              priority_score={selectedCustomer.priority}
            />
            <Button
              variant="outline"
              className="p-1 h-fit border rounded-md mr-2"
              onClick={() =>
                window.open(makeCRMURL(selectedCustomer.customer_id), '_blank')
              }
              disabled={!selectedCustomer.customer_id}
            >
              Open CRM
            </Button>
          </div>
          <div>
            <label className="text-sm text-gray-500">Customer ID</label>
            <p className="mt-1">{selectedCustomer.customer_id}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Inquiries</label>
            <p className="mt-1">{selectedCustomer.inquiry_count}</p>
          </div>
        </div>
      </div>

      {selectedCustomer.personal_details && (
        <div>
          <h2 className="mb-4">Personal Information</h2>
          <div className="grid grid-cols-2 gap-6">
            {selectedCustomer.personal_details.company_name && (
              <div>
                <label className="text-sm text-gray-500">Company</label>
                <p className="mt-1">
                  {selectedCustomer.personal_details.company_name}
                </p>
              </div>
            )}
            {selectedCustomer.personal_details.emails &&
              selectedCustomer.personal_details.emails.length > 0 && (
                <div>
                  <label className="text-sm text-gray-500">Email</label>
                  <p className="mt-1">
                    {selectedCustomer.personal_details.emails[0]}
                  </p>
                </div>
              )}
            {selectedCustomer.personal_details.phones &&
              selectedCustomer.personal_details.phones.length > 0 && (
                <div>
                  <label className="text-sm text-gray-500">Phone</label>
                  <p className="mt-1">
                    {selectedCustomer.personal_details.phones[0]}
                  </p>
                </div>
              )}
          </div>
        </div>
      )}

      {selectedCustomer.inquiries && selectedCustomer.inquiries.length > 0 && (
        <div>
          <h2 className="mb-4">Inquiries</h2>
          <div className="space-y-6">
            {selectedCustomer.inquiries.map((inquiry) => (
              <div
                key={inquiry.lead_id}
                className="border-l-4 border-gray-200 pl-4"
              >
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm text-gray-500">Lead ID</label>
                    <p className="mt-1">{inquiry.lead_id}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Source</label>
                    <p className="capitalize mt-1">{inquiry.source}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">
                      Inquiry Time
                    </label>
                    <p className="mt-1">
                      {new Date(inquiry.inquiry_time).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                {inquiry.vehicles_of_interest &&
                  inquiry.vehicles_of_interest.length > 0 && (
                    <div className="mt-4">
                      <label className="text-sm text-gray-500">
                        Vehicles of Interest
                      </label>
                      <div className="mt-2 space-y-2">
                        {inquiry.vehicles_of_interest
                          .filter((vehicle) => vehicle.year && vehicle.year > 0)
                          .map((vehicle, vIndex) => (
                            <div
                              key={vIndex}
                              className="bg-gray-50 p-3 rounded"
                            >
                              {vehicle.year} {vehicle.make} {vehicle.model}{' '}
                              {vehicle.trim}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedCustomer.inquiries &&
        selectedCustomer.inquiries.some(
          (inquiry) =>
            inquiry.vehicles_of_interest &&
            inquiry.vehicles_of_interest.length > 0,
        ) && (
          <div>
            <h2 className="mb-4">All Vehicles of Interest</h2>
            <div className="space-y-3">
              {selectedCustomer.inquiries
                .filter(
                  (inquiry) =>
                    inquiry.vehicles_of_interest &&
                    inquiry.vehicles_of_interest.length > 0,
                )
                .map((inquiry) =>
                  inquiry.vehicles_of_interest
                    ?.filter((vehicle) => vehicle.year && vehicle.year > 0)
                    ?.map((vehicle, vIndex) => (
                      <div
                        key={`${inquiry.lead_id}-${vIndex}`}
                        className="bg-blue-50 p-4 rounded border-l-4 border-blue-200"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div>
                              {vehicle.year} {vehicle.make} {vehicle.model}{' '}
                              {vehicle.trim}
                            </div>
                            {vehicle.vin && (
                              <div className="text-sm text-gray-500 mt-1">
                                VIN: {vehicle.vin}
                              </div>
                            )}
                            {vehicle.stock_number && (
                              <div className="text-sm text-gray-500">
                                Stock: {vehicle.stock_number}
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-gray-500">
                              From Lead #{inquiry.lead_id}
                            </div>
                            <div className="text-xs text-gray-400 capitalize">
                              {inquiry.source}
                            </div>
                          </div>
                        </div>
                      </div>
                    )),
                )
                .flat()}
            </div>
          </div>
        )}

      {selectedCustomer.trade_vehicles &&
        selectedCustomer.trade_vehicles.length > 0 && (
          <div>
            <h2 className="mb-4">Trade Vehicles</h2>
            <div className="space-y-3">
              {selectedCustomer.trade_vehicles.map((vehicle, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded">
                  <div>
                    {vehicle.year} {vehicle.make} {vehicle.model} {vehicle.trim}
                  </div>
                  {vehicle.vin && (
                    <div className="text-sm text-gray-500 mt-1">
                      VIN: {vehicle.vin}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

      {selectedCustomer.key_indicators && (
        <div>
          <h2 className="mb-4">Key Indicators</h2>
          <div className="grid grid-cols-2 gap-6">
            {Object.entries(selectedCustomer.key_indicators).map(
              ([key, indicator]) => (
                <div key={key} className="flex items-start space-x-3">
                  <div
                    className={`w-3 h-3 rounded-full mt-1.5 flex-shrink-0 ${indicator.satisfied ? 'bg-green-500' : 'bg-gray-300'}`}
                  />
                  <div>
                    <div className="text-sm">{indicator.description}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      Weight: {indicator.weight}
                    </div>
                  </div>
                </div>
              ),
            )}
          </div>
        </div>
      )}
    </div>
  );
};
